import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Icon } from '@/kit/Icon';
import { Radio, RadioGroup } from '@/kit/Radio';
import { Tooltip } from '@/kit/Tooltip';
import { openDialog } from '@/kit/dialog';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { PeriodRow } from '@/core/models/practice.models';
import type { RevisedAccountsDialogData, RevisedAccountsView } from '@/core/models/report-options.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { practiceService } from '@/core/services/practice';
import { reportOptionsService } from '@/core/services/reportOptions';
import { ReplacementDialog } from './ReplacementDialog';
import { SupplementaryDialog } from './SupplementaryDialog';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';

const NO_PERIOD = 'Add an accounting period for this client before revising its accounts.';

const REPLACEMENT_TIP =
  'This option will allow you to generate revised financial statements to send to Companies House to replace the original accounts. Please make all necessary revisions and enter details in the text box provided in the edit section.';
const SUPPLEMENTARY_TIP =
  'This option only applies where the accounts themselves have not been revised and will generate the original accounts along with a supplementary note to send to Companies House. Please enter details for the supplementary note and add the date of approval and name of the director approving the supplementary note.';

/** Revised accounts belong to the client's current (latest) accounting period. */
const currentPeriod = (periods: PeriodRow[]): PeriodRow | undefined => {
  const sorted = [...periods].sort((a, b) => `${b.periodTo ?? ''}`.localeCompare(`${a.periodTo ?? ''}`));
  return sorted.find((p) => !p.isComparative) ?? sorted[0];
};

/** Report Settings > Revised Accounts: choose how the current period's accounts are revised, and open the detail dialog. */
export function RevisedAccounts() {
  const navigate = useNavigate();
  const client = useReportClient();
  const [view, setViewState] = useState<RevisedAccountsView | null>(null);
  /**
   * task-112: null until the client picks one, so neither radio starts selected. Revising by
   * replacement and revising by supplementary note are different filings at Companies House,
   * and the screen must not appear to have chosen on the client's behalf. getRevisedAccounts
   * returns null until saveRevisionType has been called for this client and period.
   */
  const [revisionType, setRevisionType] = useState<number | null>(null);
  const viewRef = useRef<RevisedAccountsView | null>(null);
  const revisionTypeRef = useRef<number | null>(null);
  const alive = useRef(true);

  const setView = (next: RevisedAccountsView | null) => {
    viewRef.current = next;
    setViewState(next);
  };
  const changeRevisionType = (next: number | null) => {
    revisionTypeRef.current = next;
    setRevisionType(next);
  };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!client) return;
    practiceService.getClientPeriods(client.id).then(
      (response) => {
        if (!alive.current) return;
        const period = currentPeriod(response.periods ?? []);
        if (!period) {
          notifyError(NO_PERIOD);
          return;
        }
        reportOptionsService.getRevisedAccounts(client.id, period.id).then(
          (loaded) => {
            if (!alive.current) return;
            setView(loaded);
            changeRevisionType(loaded.revisionType);
          },
          (error) => alive.current && notifyError(error),
        );
      },
      (error) => alive.current && notifyError(error),
    );
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRevisionTypeChange = (value: number) => {
    // The radio's ngModel updates before the call.
    changeRevisionType(value);
    const current = viewRef.current;
    if (!current) return;
    reportOptionsService.saveRevisionType(current.clientId, current.accountingPeriodId, value).then(
      (saved) => {
        if (!alive.current) return;
        setView({ ...current, revisionType: saved.revisionType });
        changeRevisionType(saved.revisionType);
      },
      (error) => {
        if (!alive.current) return;
        changeRevisionType(current.revisionType);
        notifyError(error);
      },
    );
  };

  const openRevisionDialog = (component: typeof ReplacementDialog | typeof SupplementaryDialog) => {
    const current = viewRef.current;
    if (!current) {
      notifyError(NO_PERIOD);
      return;
    }
    const data: RevisedAccountsDialogData = { view: current };
    openDialog<RevisedAccountsDialogData, RevisedAccountsView>(component, { disableClose: true, data })
      .afterClosed()
      .then((saved) => {
        if (saved && alive.current) {
          // Keep the radio choice made on this screen; the dialog only saves its own details.
          setView({ ...saved, revisionType: revisionTypeRef.current });
          notifySuccess(NotificationTextMessage.successMessage);
        }
      });
  };

  return (
    <StickyReportSettingsFrame title="Revised Accounts" onClose={() => navigate(SETTINGS_ROUTE)}>
      <div className="sidenav-body pt-1">
        <div className="form-field">
          <p className="mb-10">Please select how you would like to revise your financial statements:</p>
          <RadioGroup value={revisionType} disabled={!view} onChange={(value) => onRevisionTypeChange(value as number)}>
            <Radio value={0} hostClassName="example-radio-button">
              <div className="d-flex align-items-center gap-5">
                Revision by replacement
                <Tooltip title={REPLACEMENT_TIP}>
                  <Icon name="info" outlined className="info-icon" />
                </Tooltip>
                <Icon
                  name="edit"
                  outlined
                  className="info-icon"
                  onClick={() => openRevisionDialog(ReplacementDialog)}
                />
              </div>
            </Radio>
            <Radio value={1} hostClassName="example-radio-button">
              <div className="d-flex align-items-center gap-5">
                Revision by supplementary note
                <Tooltip title={SUPPLEMENTARY_TIP}>
                  <Icon name="info" outlined className="info-icon" />
                </Tooltip>
                <Icon
                  name="edit"
                  outlined
                  className="info-icon"
                  onClick={() => openRevisionDialog(SupplementaryDialog)}
                />
              </div>
            </Radio>
          </RadioGroup>
        </div>
      </div>
    </StickyReportSettingsFrame>
  );
}
